define((require) => {
    "use strict";

    const {extend, result}                 = require("underscore");
    const Channel                          = require("ui/publish/channelSetup/Channel");
    const ChannelSetupManualViewController = require("ui/publish/channelSetup/ChannelSetupManualViewController");
    const Promise                          = require("shared/util/Promise");
    const EditorEvents                     = require("shared/services/EditorEvents");
    const ContentQuery                     = require("data/ContentQuery");
    const SequentialButton                 = require("util/SequentialButton");
    const SystemMessages                   = require("editor/services/SystemMessages");
    const template                         = require("ui/publish/channelSetup/ChannelSetupManualView.tpl");
    const ChannelChangesInfo               = require("ui/publish/channelSetup/ChannelChangesInfo");
    const ImprovedView                     = require("editor/zbone/ImprovedView");
    const ChannelCollectionView            = require("ui/publish/ChannelCollectionView");
    const NotificationBar                  = require("editor/ui/notificationbar/NotificationBar");

    /**
     * View of all the channels, showing the channels in a list, and the buttons to add a new channel and to publish
     * (which will be disabled if insufficient privileges).
     *
     */
    return ImprovedView.extend("ChannelSetupManualView", {

        template,

        channelChangesInfo: null,

        controller: ChannelSetupManualViewController,

        regions: {
            contentRegion: ".content"
        },

        ui: {
            createNewSlot: ".create-new-slot",
            saveAndPublishChannelsButton: "#publish-channels",
            channelItems: ".channel-items"
        },

        initialize() {
            this.options = extend(this.options, this.viewOptions || {});
            this.channelChangesInfo = new ChannelChangesInfo();
        },

        events: {
            "click @ui.createNewSlot": "_createNewSlotClickHandler",
            "click @ui.saveAndPublishChannelsButton": "_saveAndPublishChannelsClickHandler"
        },

        templateHelpers() {
            const {
                allowedToPublish, showAttachButton, showCreateSlotField, showPublishButton, showAttachActions, showAttachHeader, showCreateSlotInProjectLabel
            } = this.options;
            return {
                attachHeader: "All Slots",
                itemCount: this.collection.length(),
                allowedToPublish,
                showAttachButton,
                showCreateSlotField,
                showPublishButton,
                showAttachActions,
                showAttachHeader,
                showCreateSlotInProjectLabel
            };
        },

        onRender() {
            this._handleUnPushedChannels(this.options.hasUnPushedChannels);

            this._saveAndPublishChannelsButton = SequentialButton.add(this.ui.saveAndPublishChannelsButton, () => {
                this._trackEditorEvent();

                let purgeTime = null;

                return this.channelChangesInfo.applyChanges()
                    .then(() => Channel.push())
                    .then((channelPushResponse) => {
                        purgeTime = channelPushResponse[0].purgeTime;
                        this.options.hasUnPushedChannels = false;
                        return this._syncProjectContext();
                    })
                    .then(() => this.refresh())
                    .done(() => {
                        // remove un-pushed notification bar if open
                        if (this._unPushedNotificationBarId) {
                            NotificationBar.close({id: this._unPushedNotificationBarId, clear: "self"});
                        }

                        // Update the Channels counter.
                        this.collection.trigger("sync:state");
                        // Notify the user that a purge has been initiated
                        SystemMessages.notifyPurge("Changes", "saved", purgeTime, true);

                        this.channelChangesInfo.clear();
                        this.shouldButtonChange();
                    });
            });
        },

        onShow() {
            const {
                project,
                collection,
                collectionViewOptions
            } = this.options;

            const channelCollectionViewOptions = extend({
                collection,
                itemViewOptions: () => {
                    const {
                        node, publishableDescriptors, initialPublishables, publishableCollection, groups, allowedToPublish, hasUnPushedChannels
                    } = this.options;
                    // Since most of options are needed, we just return all options for simplicity.
                    return extend(this.options, {
                        channelChangesInfo: this.channelChangesInfo,
                        node,
                        publishableDescriptors,
                        initialPublishables,
                        publishableCollection,
                        groups,
                        allowedToPublish,
                        hasUnPushedChannels,
                        parentView: this
                    });
                },
                horizontalBuffer: 200,
                scrollTarget: this._determineScrollTarget()
            }, collectionViewOptions || this.collectionViewOptions);

            if (project) {
                this.collection.options.project = project;
            }

            this._collectionView = new ChannelCollectionView(channelCollectionViewOptions);
            this.contentRegion.show(this._collectionView);
        },

        shouldButtonChange(hasUnPushedChannels) {
            const hasChannelBeenModified = this.channelChangesInfo.hasAny() || hasUnPushedChannels; // added/removed/edited
            this.ui.saveAndPublishChannelsButton.toggleClass("unsaved-changes", hasChannelBeenModified);
        },

        refresh(options = {}) {
            const query = options.sorting || ContentQuery.sort().field("editedAt");
            return this.collection.query(query);
        },

        _handleUnPushedChannels(hasUnPushedChanges) {
            if (hasUnPushedChanges) {
                this._unPushedNotificationBarId = NotificationBar.open("There are unpushed slots. Please click the Save/Publish button to re-push them.", {closeable: true});
                this.shouldButtonChange(hasUnPushedChanges);

                const eventProperties = {
                    type: "UnPushedChannels",
                    triggerOrigin: "NotificationBar"
                };

                EditorEvents.trigger(EditorEvents.constants.ERROR, eventProperties);
            }
        },

        /**
         * @private
         *
         * Keeps the project in sync, if any, reflecting the current collection, i.e. <tt>this.collection</tt>.
         *
         * @return {Promise} A promise that resolves once the project is synced. A resolved promise
         *                   is returned if we're not in a project context.
         */
        _syncProjectContext() {
            const projectContext = this.options.projectContext;

            // Handle project, if any...
            if (projectContext) {
                // Fetch project first to avoid potential versioning issues...
                return projectContext.project
                    .fetch()
                    .then(() => {
                        return projectContext.attach(this.collection.models());
                    });
            }

            return Promise.resolved();
        },

        _trackEditorEvent() {
            const projectContext =  this.options.projectContext;
            const channels = this.channelChangesInfo.asList().map((channel) => (channel.get("name")));

            const eventProperties = {
                channels: channels,
                triggerOrigin: "SaveAndPublishButton",
                dragAnDrop: false,
                manual: true,
                projectId: projectContext && projectContext.project.get("id")
            };

            EditorEvents.trigger(EditorEvents.constants.PUBLISH_CHANNEL, eventProperties);
        },

        _determineScrollTarget() {
            const scrollTarget = result(this.options, "scrollTarget") || result(this.viewScope, "scrollTarget");

            return scrollTarget || this.ui.channelItems;
        },

        _createNewSlotClickHandler() {
            this.trigger("createNewSlotOpened");
        },

        _saveAndPublishChannelsClickHandler() {
            this._saveAndPublishChannelsButton();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/publish/channelSetup/ChannelSetupManualView.js